/**
 * useForYouShelves - Phase 1 Foundation
 *
 * Implements algorithmic shelves for the For You page:
 * - "Because you loved [Movie]" (anchor from highest rating)
 * - "New releases for you" (recent + taste filters)
 * - "Critically acclaimed you missed" (quality filter + exclude rated)
 * - "Popular right now" (cold start fallback)
 *
 * Key constraints (from FOR_YOU_PAGE_SPEC.md):
 * - No LLM calls (Phase 2+ only)
 * - Page-level dedupe (no title repeats across shelves)
 * - Cold start handling (popularity fallback for 0-rating users)
 * - movie_similarities table NOT populated - uses Meilisearch genre fallback
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useUserLibrary } from '../contexts/UserLibraryContext';
import { queryKeys } from '../lib/queryKeys';
import { apiFetch } from '../lib/api';
import { discoverMedia, getMovieDetails } from '../api/tmdb';
import type { MediaItem } from '../types/movie';

// =============================================================================
// TYPES
// =============================================================================

export interface ForYouShelf {
  id: string;
  title: string;
  subtitle?: string;
  movies: MediaItem[];
  anchor?: MediaItem; // For "Because you loved X" shelf
  shelfType: 'similarity' | 'discovery' | 'temporal' | 'cold_start';
}

export interface ForYouData {
  hero: MediaItem | null;
  heroReason: string | null;
  shelves: ForYouShelf[];
  isColdStart: boolean;
  hasOnboarding: boolean;
}

interface UserRating {
  id: string;
  user_id: string;
  media_id: number;
  media_type: 'movie' | 'tv';
  rating: number;
  created_at: string;
  updated_at: string;
}

interface RatingsResponse {
  ratings: UserRating[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

interface TasteProfile {
  favorite_genre_ids: number[];
  favorite_movie_ids: number[];
  favorite_actor_ids: number[];
  favorite_director_ids: number[];
}

// =============================================================================
// CONSTANTS
// =============================================================================

const SHELF_SIZE = 12; // Items per shelf
const MIN_RATINGS_FOR_PERSONALIZATION = 1;
const CURRENT_YEAR = new Date().getFullYear();

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Deduplicate movies across shelves using a seen Set
 * Returns movies not in the seen set and updates the set
 */
function dedupeMovies(movies: MediaItem[], seen: Set<number>, limit: number): MediaItem[] {
  const result: MediaItem[] = [];
  for (const movie of movies) {
    if (!seen.has(movie.id) && result.length < limit) {
      seen.add(movie.id);
      result.push(movie);
    }
  }
  return result;
}

/**
 * Filter out movies the user has already rated or has in watchlist
 */
function filterUserContent(
  movies: MediaItem[],
  ratedIds: Set<number>,
  watchlistIds: Set<number>
): MediaItem[] {
  return movies.filter(m => !ratedIds.has(m.id) && !watchlistIds.has(m.id));
}

// =============================================================================
// DATA FETCHERS
// =============================================================================

async function fetchTopRatings(token: string): Promise<UserRating[]> {
  const response = await apiFetch<RatingsResponse>('/ratings?limit=20&media_type=movie', { token });
  // Sort by rating descending, then by date for tie-breaker
  return response.ratings.sort((a, b) => {
    if (b.rating !== a.rating) return b.rating - a.rating;
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  });
}

async function fetchTasteProfile(token: string): Promise<TasteProfile | null> {
  try {
    const response = await apiFetch<{ profile: TasteProfile | null }>('/taste-profile', { token });
    return response.profile;
  } catch {
    // Taste profile may not exist for new users
    return null;
  }
}

/**
 * Build "Because you loved X" shelf using Meilisearch genre-based discovery
 * FALLBACK: movie_similarities table is not populated, so we use genre matching
 */
async function buildSimilarityShelf(
  anchorMovie: MediaItem,
  seen: Set<number>,
  ratedIds: Set<number>,
  watchlistIds: Set<number>
): Promise<ForYouShelf | null> {
  try {
    // Use anchor movie's genres to find similar movies
    const genreIds = anchorMovie.genre_ids || [];
    if (genreIds.length === 0) return null;

    const response = await discoverMedia(
      {
        genres: genreIds.slice(0, 2), // Use top 2 genres for better matches
        sortBy: 'vote_average.desc',
        popularOnly: true, // Popular gate v1: vote_count >= 50 OR popularity >= 4
        ratingRange: [7, 10], // High quality only
        rtRatingRange: [0, 100],
        ourRatingRange: [0, 10],
        yearRange: [1980, CURRENT_YEAR],
        runtimeRange: [0, 300],
        languages: [],
        actorSearch: [],
        directorSearch: [],
      },
      1,
      'movie'
    );

    // Filter and dedupe
    let movies = filterUserContent(response.results, ratedIds, watchlistIds);
    // Remove the anchor movie itself
    movies = movies.filter(m => m.id !== anchorMovie.id);
    movies = dedupeMovies(movies, seen, SHELF_SIZE);

    if (movies.length < 4) return null; // Don't show sparse shelves

    return {
      id: 'because-you-loved',
      title: `Because you loved ${anchorMovie.title}`,
      movies,
      anchor: anchorMovie,
      shelfType: 'similarity',
    };
  } catch (error) {
    console.error('[ForYou] Error building similarity shelf:', error);
    return null;
  }
}

/**
 * Build "New releases for you" shelf
 * Recent movies (last 90 days) filtered by user's taste
 */
async function buildNewReleasesShelf(
  seen: Set<number>,
  ratedIds: Set<number>,
  watchlistIds: Set<number>,
  favoriteGenres: number[]
): Promise<ForYouShelf | null> {
  try {
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    const startYear = ninetyDaysAgo.getFullYear();

    const response = await discoverMedia(
      {
        genres: favoriteGenres.slice(0, 3), // User's favorite genres (if any)
        sortBy: 'popularity.desc',
        popularOnly: true, // Popular gate v1: vote_count >= 50 OR popularity >= 4
        ratingRange: [0, 10],
        rtRatingRange: [0, 100],
        ourRatingRange: [0, 10],
        yearRange: [startYear, CURRENT_YEAR],
        runtimeRange: [0, 300],
        languages: [],
        actorSearch: [],
        directorSearch: [],
      },
      1,
      'movie'
    );

    let movies = filterUserContent(response.results, ratedIds, watchlistIds);
    movies = dedupeMovies(movies, seen, SHELF_SIZE);

    if (movies.length < 4) return null;

    return {
      id: 'new-releases',
      title: 'New releases for you',
      subtitle: 'Fresh picks based on your taste',
      movies,
      shelfType: 'temporal',
    };
  } catch (error) {
    console.error('[ForYou] Error building new releases shelf:', error);
    return null;
  }
}

/**
 * Build "Critically acclaimed you missed" shelf
 * High RT/IMDB movies the user hasn't seen
 */
async function buildCriticallyAcclaimedShelf(
  seen: Set<number>,
  ratedIds: Set<number>,
  watchlistIds: Set<number>
): Promise<ForYouShelf | null> {
  try {
    const response = await discoverMedia(
      {
        genres: [],
        sortBy: 'vote_average.desc',
        popularOnly: true, // Popular gate v1: vote_count >= 50 OR popularity >= 4
        ratingRange: [7.5, 10], // High IMDB
        rtRatingRange: [80, 100], // High RT
        ourRatingRange: [0, 10],
        yearRange: [2000, CURRENT_YEAR], // Modern classics
        runtimeRange: [0, 300],
        languages: [],
        actorSearch: [],
        directorSearch: [],
      },
      1,
      'movie'
    );

    let movies = filterUserContent(response.results, ratedIds, watchlistIds);
    movies = dedupeMovies(movies, seen, SHELF_SIZE);

    if (movies.length < 4) return null;

    return {
      id: 'critically-acclaimed',
      title: 'Critically acclaimed you missed',
      subtitle: 'Highly rated films you haven\'t seen yet',
      movies,
      shelfType: 'discovery',
    };
  } catch (error) {
    console.error('[ForYou] Error building critically acclaimed shelf:', error);
    return null;
  }
}

/**
 * Build "Popular right now" shelf (cold start fallback)
 */
async function buildPopularShelf(
  seen: Set<number>,
  ratedIds: Set<number>,
  watchlistIds: Set<number>
): Promise<ForYouShelf | null> {
  try {
    const response = await discoverMedia(
      {
        genres: [],
        sortBy: 'popularity.desc',
        popularOnly: true, // Popular gate v1: vote_count >= 50 OR popularity >= 4
        ratingRange: [6, 10],
        rtRatingRange: [0, 100],
        ourRatingRange: [0, 10],
        yearRange: [CURRENT_YEAR - 2, CURRENT_YEAR],
        runtimeRange: [0, 300],
        languages: [],
        actorSearch: [],
        directorSearch: [],
      },
      1,
      'movie'
    );

    let movies = filterUserContent(response.results, ratedIds, watchlistIds);
    movies = dedupeMovies(movies, seen, SHELF_SIZE);

    if (movies.length < 4) return null;

    return {
      id: 'popular-now',
      title: 'Popular right now',
      movies,
      shelfType: 'cold_start',
    };
  } catch (error) {
    console.error('[ForYou] Error building popular shelf:', error);
    return null;
  }
}

/**
 * Build "Highest rated of all time" shelf (cold start)
 */
async function buildAllTimeGreatsShelf(
  seen: Set<number>,
  ratedIds: Set<number>,
  watchlistIds: Set<number>
): Promise<ForYouShelf | null> {
  try {
    const response = await discoverMedia(
      {
        genres: [],
        sortBy: 'vote_average.desc',
        popularOnly: true, // Popular gate v1: vote_count >= 50 OR popularity >= 4
        ratingRange: [8, 10],
        rtRatingRange: [90, 100],
        ourRatingRange: [0, 10],
        yearRange: [1920, CURRENT_YEAR],
        runtimeRange: [0, 300],
        languages: [],
        actorSearch: [],
        directorSearch: [],
      },
      1,
      'movie'
    );

    let movies = filterUserContent(response.results, ratedIds, watchlistIds);
    movies = dedupeMovies(movies, seen, SHELF_SIZE);

    if (movies.length < 4) return null;

    return {
      id: 'all-time-greats',
      title: 'Highest rated of all time',
      movies,
      shelfType: 'cold_start',
    };
  } catch (error) {
    console.error('[ForYou] Error building all-time greats shelf:', error);
    return null;
  }
}

// =============================================================================
// MAIN SHELF BUILDER
// =============================================================================

async function buildForYouShelves(
  ratings: UserRating[],
  tasteProfile: TasteProfile | null,
  watchlistIds: Set<number>
): Promise<ForYouData> {
  const seen = new Set<number>(); // Page-level dedupe
  const ratedIds = new Set(ratings.map(r => r.media_id));
  const shelves: ForYouShelf[] = [];
  const isColdStart = ratings.length < MIN_RATINGS_FOR_PERSONALIZATION;
  const hasOnboarding = tasteProfile !== null && (
    tasteProfile.favorite_genre_ids.length > 0 ||
    tasteProfile.favorite_movie_ids.length > 0
  );

  let hero: MediaItem | null = null;
  let heroReason: string | null = null;

  // Get favorite genres from taste profile or empty
  const favoriteGenres = tasteProfile?.favorite_genre_ids || [];

  if (!isColdStart) {
    // PERSONALIZED FLOW: User has ratings

    // Get anchor movie (highest rated) for similarity shelf
    const topRating = ratings[0];
    if (topRating && topRating.rating >= 7) {
      try {
        // Fetch anchor movie details for genre_ids
        const anchorDetails = await getMovieDetails(topRating.media_id);
        const anchorMovie: MediaItem = {
          id: anchorDetails.id,
          title: anchorDetails.title,
          overview: anchorDetails.overview,
          poster_path: anchorDetails.poster_path,
          backdrop_path: anchorDetails.backdrop_path,
          release_date: anchorDetails.release_date,
          genre_ids: anchorDetails.genre_ids || [],
          vote_average: anchorDetails.vote_average,
          vote_count: anchorDetails.vote_count,
          popularity: anchorDetails.popularity,
          original_language: anchorDetails.original_language,
          runtime: anchorDetails.runtime,
          media_type: 'movie',
          imdb_rating: anchorDetails.imdb_rating,
          rt_rating: anchorDetails.rt_rating,
          filmpl_rating: anchorDetails.filmpl_rating,
        };

        // Set hero to anchor movie
        hero = anchorMovie;
        heroReason = `Because you gave it ${topRating.rating} stars`;
        seen.add(anchorMovie.id);

        // Build similarity shelf
        const similarityShelf = await buildSimilarityShelf(anchorMovie, seen, ratedIds, watchlistIds);
        if (similarityShelf) shelves.push(similarityShelf);
      } catch (error) {
        console.error('[ForYou] Error fetching anchor movie:', error);
      }
    }

    // Build new releases shelf
    const newReleasesShelf = await buildNewReleasesShelf(seen, ratedIds, watchlistIds, favoriteGenres);
    if (newReleasesShelf) shelves.push(newReleasesShelf);

    // Build critically acclaimed shelf
    const criticallyAcclaimedShelf = await buildCriticallyAcclaimedShelf(seen, ratedIds, watchlistIds);
    if (criticallyAcclaimedShelf) shelves.push(criticallyAcclaimedShelf);

    // Add popular shelf as backup if we don't have enough shelves
    if (shelves.length < 3) {
      const popularShelf = await buildPopularShelf(seen, ratedIds, watchlistIds);
      if (popularShelf) shelves.push(popularShelf);
    }
  } else {
    // COLD START FLOW: User has no ratings

    // Build shelves from taste profile favorites or fallback to popular
    const popularShelf = await buildPopularShelf(seen, ratedIds, watchlistIds);
    if (popularShelf) shelves.push(popularShelf);

    const criticallyAcclaimedShelf = await buildCriticallyAcclaimedShelf(seen, ratedIds, watchlistIds);
    if (criticallyAcclaimedShelf) shelves.push(criticallyAcclaimedShelf);

    const allTimeGreatsShelf = await buildAllTimeGreatsShelf(seen, ratedIds, watchlistIds);
    if (allTimeGreatsShelf) shelves.push(allTimeGreatsShelf);

    // Set hero from first popular movie if available
    if (popularShelf && popularShelf.movies.length > 0) {
      hero = popularShelf.movies[0];
      heroReason = 'Trending now';
      // Remove from shelf to avoid duplicate
      popularShelf.movies = popularShelf.movies.slice(1);
    }
  }

  return {
    hero,
    heroReason,
    shelves,
    isColdStart,
    hasOnboarding,
  };
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Derive a stable version string from taste profile for query key.
 * Ensures shelves recompute when taste profile changes.
 */
function getTasteProfileVersion(profile: TasteProfile | null | undefined): string {
  if (!profile) return 'none';
  // Stable key from sorted arrays to detect any taste profile change
  const genres = (profile.favorite_genre_ids || []).sort((a, b) => a - b).join(',');
  const movies = (profile.favorite_movie_ids || []).sort((a, b) => a - b).join(',');
  const actors = (profile.favorite_actor_ids || []).sort((a, b) => a - b).join(',');
  const directors = (profile.favorite_director_ids || []).sort((a, b) => a - b).join(',');
  return `g:${genres}|m:${movies}|a:${actors}|d:${directors}`;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * useForYouShelves - For authenticated users only.
 *
 * IMPORTANT: This hook should only be called in components that are guaranteed
 * to render for authenticated users. Use ForYouPageAuthed pattern to ensure
 * logged-out users don't trigger any shelf-building network requests.
 *
 * @param enabled - External gate to skip all queries (default: true).
 *                  Set to false when /api/for-you succeeds to avoid wasted network calls.
 */
export function useForYouShelves(enabled: boolean = true) {
  const { user, session } = useAuth();
  const { getWatchlistIds, isHydrated: libraryHydrated } = useUserLibrary();

  // Get watchlist IDs for filtering (bounded, movie-only, max 2000)
  const watchlistIds = new Set<number>(
    libraryHydrated ? getWatchlistIds('movie', 2000) : []
  );

  // Gate: only fetch if authenticated AND externally enabled
  const isAuthenticated = !!user && !!session?.access_token;
  const queriesEnabled = isAuthenticated && enabled;

  // Fetch user's top ratings
  const ratingsQuery = useQuery({
    queryKey: queryKeys.forYou.topRatings(user?.id || 'anon'),
    queryFn: () => fetchTopRatings(session?.access_token || ''),
    enabled: queriesEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  // Fetch taste profile (for personalization + cold start with onboarding)
  const tasteProfileQuery = useQuery({
    queryKey: queryKeys.forYou.tasteProfile(user?.id || 'anon'),
    queryFn: () => fetchTasteProfile(session?.access_token || ''),
    enabled: queriesEnabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });

  // Derive taste profile version for query key (ensures recompute on taste change)
  const tasteProfileVersion = getTasteProfileVersion(tasteProfileQuery.data);

  // Build shelves when BOTH ratings AND taste profile are resolved
  // tasteProfileQuery.isFetched = true when query has completed (success OR error)
  const shelvesQuery = useQuery({
    queryKey: queryKeys.forYou.shelves(user?.id || 'anon', tasteProfileVersion),
    queryFn: () => buildForYouShelves(
      ratingsQuery.data || [],
      tasteProfileQuery.data || null,
      watchlistIds
    ),
    enabled: queriesEnabled && ratingsQuery.isSuccess && tasteProfileQuery.isFetched && libraryHydrated,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  // No coldStartQuery - logged-out users see LoggedOutState, not shelves
  // This hook should only be called for authenticated users (ForYouPageAuthed pattern)

  const isLoading = !isAuthenticated ||
    ratingsQuery.isLoading ||
    !tasteProfileQuery.isFetched ||
    shelvesQuery.isLoading ||
    !libraryHydrated;

  return {
    data: shelvesQuery.data || null,
    isLoading,
    isError: shelvesQuery.isError,
    error: shelvesQuery.error,
    isColdStart: shelvesQuery.data?.isColdStart ?? true,
    hasOnboarding: shelvesQuery.data?.hasOnboarding ?? false,
  };
}
